import React, {Suspense, useEffect} from "react"

import dayjs from "dayjs"
import {isEmpty} from "lodash"
import PropTypes from "prop-types"
import {connect, useDispatch, useSelector} from "react-redux"
import {Routes} from "react-router"
import {Route} from "react-router-dom"
import * as Events from "react-scroll"
import {Dimmer, Loader} from "semantic-ui-react"

import {useMatomo} from "@datapunt/matomo-tracker-react"

import {
    getPersonnalisationByNumClient,
    getPersonnalisationClientsLogos,
    getPersonnalisationLogoByNumClient,
} from "../actions/user/personnalisationActions"
import {ACCESSIBILITY_LINKS} from "../constants/accessibilityLinks"
import {LOCAL_STORAGE_PERSO_NUMERO_CLIENT} from "../constants/localStorageValuesKeys"
import {Page} from "../constants/page/pages"
import {PagesArchitecture} from "../constants/page/pagesArchitecture"
import {EBSRequestKeys} from "../constants/requests"
import {saveCookiesChoices} from "../shared-components/actions/localStorage/cookies/cookiesActions"
import AccessibilityLinks from "../shared-components/components/AccessibilityLinks"
import CheckBrowserContainer from "../shared-components/components/CheckBrowser/checkBrowserContainer"
import NotificationsContainer from "../shared-components/components/Notifications/notificationsContainer"
import {useIsResponsive} from "../shared-components/hooks/useIsResponsive"
import {useRouter} from "../shared-components/hooks/useRouter"
import {clearLocalStorageValue, saveLocalStorageValue} from "../store/localStorage/localStorageActions"
import {showCookiesModal} from "../store/selectors/localStorage/cookiesSelectors"
import {getLocalStorageValue} from "../store/selectors/localStorage/genericLocalStorageSelectors"
import {getNotifications} from "../store/selectors/notifications/notificationsSelectors"
import {getPayload, isFetching} from "../store/selectors/requests/requestsSelectors"
import {getSelectedArea, isEbUnique, isUserConnected} from "../store/selectors/sessionStorage/userSelectors"
import {getActualPage} from "../store/selectors/temporaries/pageSelectors"
import CookiesModal from "./../shared-components/components/CookiesModal"
import {AsyncRoutingLayout} from "./asyncScenes"

import "./mainLayout.scss"
import withRouter from "../shared-components/wrappers/withRouter"

const MainLayout = () => {
    const {navigate, location} = useRouter()
    const dispatch = useDispatch()
    const isResponsive = useIsResponsive()
    const {search} = location

    const actualPage = useSelector(state => getActualPage(state))
    const selectedArea = useSelector(state => getSelectedArea(state))
    const isConnected = useSelector(state => isUserConnected(state))
    const isCookiesModalOpened = useSelector(state => showCookiesModal(state))

    const ebUnique = useSelector(state => isEbUnique(state))
    const numClientFromLocalStorage = useSelector(state =>
        getLocalStorageValue(state, LOCAL_STORAGE_PERSO_NUMERO_CLIENT),
    )

    const personnalisationClientsLogos = useSelector(state =>
        getPayload(state, EBSRequestKeys.PERSONNALISATION_CLIENTS_LOGOS),
    )

    const isFetchingPersonnalisation = useSelector(state => isFetching(state, EBSRequestKeys.PERSONNALISATION))
    const isFetchingPersonnalisationLogo = useSelector(state => isFetching(state, EBSRequestKeys.PERSONNALISATION_LOGO))

    const {trackPageView} = useMatomo()

    useEffect(() => {
        if (isConnected && ebUnique) {
            dispatch(saveLocalStorageValue(LOCAL_STORAGE_PERSO_NUMERO_CLIENT, selectedArea.numClient))
        }
    }, [isConnected, ebUnique, dispatch, selectedArea?.numClient])

    // On vérifie si le choix de l'espace (client) a bien été fait, sinon on redirige vers la page de choix
    useEffect(() => {
        if (isConnected && numClientFromLocalStorage === undefined && !ebUnique && actualPage !== Page.CLIENT_CHOICE) {
            navigate(PagesArchitecture[Page.CLIENT_CHOICE].route)
        }
    }, [location, isConnected, numClientFromLocalStorage, ebUnique])

    // On vérifie si le choix de l'espace (beneficiaire) a bien été fait, sinon on redirige vers la page de choix
    useEffect(() => {
        if (
            isConnected &&
            numClientFromLocalStorage !== undefined &&
            selectedArea === undefined &&
            !ebUnique &&
            actualPage !== Page.AREA_CHOICE
        ) {
            navigate(PagesArchitecture[Page.AREA_CHOICE].route)
        }
    }, [selectedArea, location, location?.pathname, isConnected, actualPage, numClientFromLocalStorage, ebUnique])

    // A chaque changement de page, on remonte vers le haut
    useEffect(() => {
        // On track la page via Matomo
        trackPageView()

        Events.animateScroll.scrollToTop({containerId: isResponsive ? "navigation-layout" : null})
    }, [actualPage, isResponsive, trackPageView])

    // On vérifie si le numéro de client est présent ou non dans l'URL afin d'appliquer la personnalisation
    useEffect(() => {
        const params = new URLSearchParams(search.substring(1))
        const numClient = params.get("numClient")

        // Si le numClient est égal à 0, on réinitialise, sinon on sauvegarde le numéro
        if (numClient === "0") {
            dispatch(clearLocalStorageValue(LOCAL_STORAGE_PERSO_NUMERO_CLIENT, numClient))
        } else if (numClient !== null) {
            dispatch(saveLocalStorageValue(LOCAL_STORAGE_PERSO_NUMERO_CLIENT, numClient))
        }
    }, [search, dispatch])

    // Si le numéro de client a changé, alors on récupère la personnalisation
    // On récupère les logos client pour les afficher lors de la selection, et pour conditionner l'affichage de certains elements si nbClients > 1
    useEffect(() => {
        if (isConnected && isEmpty(personnalisationClientsLogos) && !ebUnique) {
            dispatch(getPersonnalisationClientsLogos())
        }

        if (numClientFromLocalStorage !== undefined) {
            dispatch(getPersonnalisationByNumClient(numClientFromLocalStorage))
            dispatch(getPersonnalisationLogoByNumClient(numClientFromLocalStorage)).then(
                () => {},
                () => {},
            )
        }
    }, [numClientFromLocalStorage, isConnected, dispatch])

    const onCookiesClose = () =>
        dispatch(saveCookiesChoices({isModalOpened: false, expirationDate: dayjs().add(6, "month"), enabled: true}))

    return (
        <>
            <AccessibilityLinks linksProps={ACCESSIBILITY_LINKS} />
            <CheckBrowserContainer userAgent={window.navigator.userAgent} />
            <Dimmer.Dimmable>
                <Dimmer active={isFetchingPersonnalisation || isFetchingPersonnalisationLogo} inverted page={true}>
                    <Loader active={isFetchingPersonnalisation || isFetchingPersonnalisationLogo} />
                </Dimmer>
                <div className="main-layout">
                    <Suspense fallback={<div />}>
                        <div className="main-content-notifications">
                            <NotificationsContainer getNotifications={getNotifications} />
                        </div>
                        <Routes>
                            <Route element={<AsyncRoutingLayout />} path="/*" />
                        </Routes>
                    </Suspense>
                </div>
                <CookiesModal isOpen={isCookiesModalOpened} onClose={onCookiesClose} />
            </Dimmer.Dimmable>
        </>
    )
}

MainLayout.propsTypes = {
    actualPage: PropTypes.string.isRequired,
    isMenuResponsive: PropTypes.bool.isRequired,
}

const mapStateToProps = state => ({
    actualPage: getActualPage(state),
    isUserConnected: isUserConnected(state),
})

export default withRouter(connect(mapStateToProps)(MainLayout))
